import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControlLabel,
  Switch,
  MenuItem,
 //CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
 // Edit as EditIcon,
 // Delete as DeleteIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { useAuth } from '@/context/AuthContext';
import PageHeader from '@/components/common/PageHeader';
import DataTable, { Column } from '@/components/common/DataTable';
import { usersApi, Customer, Reseller } from '@/api/users-api';

export default function CustomersPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [resellers, setResellers] = useState<Reseller[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    contactDetails: '',
    resellerId: '',
    distributorDirect: true,
  });

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const data = await usersApi.getCustomers();
      setCustomers(data);
    } catch (error) {
      console.error('Error fetching customers:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchResellers = async () => {
    if (user?.role === 'distributor') {
      try {
        const data = await usersApi.getResellers();
        setResellers(data);
      } catch (error) {
        console.error('Error fetching resellers:', error);
      }
    }
  };

  useEffect(() => {
    fetchCustomers();
    fetchResellers();
  }, [user?.role]);

  const handleCreateCustomer = async () => {
    try {
      await usersApi.createCustomer({
        name: formData.name,
        contactDetails: formData.contactDetails,
        resellerId: formData.distributorDirect ? null : formData.resellerId,
        distributorDirect: formData.distributorDirect,
      });
      setOpenDialog(false);
      resetForm();
      await fetchCustomers();
    } catch (error) {
      console.error('Error creating customer:', error);
    }
  };

  const handleOpenDialog = () => {
    resetForm();
    setOpenDialog(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      contactDetails: '',
      resellerId: '',
      distributorDirect: true,
    });
  };

  const columns: Column<Customer>[] = [
    {
      id: 'name',
      label: 'Customer Name',
      minWidth: 150,
    },
    {
      id: 'contactDetails',
      label: 'Contact Information',
      minWidth: 200,
    },
    {
      id: 'distributorDirect',
      label: 'Customer Type',
      minWidth: 120,
      format: (value) => (value ? 'Direct' : 'Reseller'),
    },
  ];

  // Add reseller column for distributor view
  if (user?.role === 'distributor') {
    columns.push({
      id: 'resellerId',
      label: 'Reseller',
      minWidth: 150,
      format: (value, row) => {
        if (row.distributorDirect) return 'N/A';
        
        const reseller = resellers.find(r => r.resellerId === value);
        return reseller ? reseller.name : 'Unknown';
      },
    });
  }

  return (
    <>
      <PageHeader
        title="Customers"
        breadcrumbs={[
          { label: 'Dashboard', href: '/dashboard' },
          { label: 'Customers' },
        ]}
        action={{
          label: 'Add Customer',
          onClick: handleOpenDialog,
          icon: <AddIcon />,
        }}
      />

      <DataTable
        columns={columns}
        data={customers}
        loading={loading}
        getRowId={(row) => row.customerId}
        onRowClick={(row) => navigate(`/customers/${row.customerId}`)}
        actions={[
          {
            icon: <ViewIcon />,
            label: 'View Details',
            onClick: (row) => navigate(`/customers/${row.customerId}`),
          },
        ]}
        emptyMessage="No customers found. Add your first customer to get started."
      />

      {/* Create Customer Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add New Customer</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, pb: 2 }}>
            <TextField
              fullWidth
              margin="normal"
              label="Customer Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
            <TextField
              fullWidth
              margin="normal"
              label="Contact Information"
              value={formData.contactDetails}
              onChange={(e) => setFormData({ ...formData, contactDetails: e.target.value })}
              multiline
              rows={3}
              required
            />

            {user?.role === 'distributor' && (
              <>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.distributorDirect}
                      onChange={(e) => setFormData({ ...formData, distributorDirect: e.target.checked })}
                    />
                  }
                  label="Direct Customer"
                  sx={{ my: 2, display: 'block' }}
                />

                {!formData.distributorDirect && (
                  <TextField
                    select
                    fullWidth
                    margin="normal"
                    label="Reseller"
                    value={formData.resellerId}
                    onChange={(e) => setFormData({ ...formData, resellerId: e.target.value })}
                    required={!formData.distributorDirect}
                  >
                    {resellers.map((reseller) => (
                      <MenuItem key={reseller.resellerId} value={reseller.resellerId}>
                        {reseller.name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateCustomer}
            disabled={!formData.name || !formData.contactDetails || (!formData.distributorDirect && !formData.resellerId)}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
